import React, { useState } from 'react'
import {
  Table,
  Button,
  Row,
  Col,
  Form,
  Pagination
} from 'react-bootstrap'
import { Link } from 'react-router-dom'
import Message from '../../components/Message'
import Loader from '../../components/Loader'
import {
  useGetStocksQuery,
  useDeleteStockMutation
} from '../../slices/stocksApiSlice'
import { toast } from 'react-toastify'
import * as XLSX from 'xlsx'

const StockListScreen = () => {
  // ─── State ────────────────────────────────────────────────────────────────
  const [filter, setFilter] = useState('')
  const [refineFilter, setRefineFilter] = useState('') // second filter
  const [pageNumber, setPageNumber] = useState(1)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  // ─── Data Fetching ─────────────────────────────────────────────────────────
  const {
    data,
    isLoading,
    error,
    refetch
  } = useGetStocksQuery(pageNumber)
  const [deleteStock, { isLoading: loadingDelete }] =
    useDeleteStockMutation()

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const deleteHandler = async (id) => {
    if (!window.confirm('Delete this stock?')) return
    try {
      await deleteStock(id).unwrap()
      toast.success('Stock deleted!')
      refetch()
    } catch (err) {
      toast.error(err?.data?.message || 'Failed to delete')
    }
  }

  const paginationHandler = (page) => setPageNumber(page)

  // ─── Filtering Logic (ORIGINAL) ────────────────────────────────────────────
  const filterStocks = () => {
    if (!data?.stocks) return []
    return data.stocks.filter((stock) => {
      const stockDate = new Date(stock.date)
      const inRange =
        (!startDate || stockDate >= new Date(startDate)) &&
        (!endDate || stockDate <= new Date(endDate))

      const noteMatch = stock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemMatch = stock.stockItems.some((i) =>
        i.name.toLowerCase().includes(filter.toLowerCase())
      )

      return inRange && (noteMatch || itemMatch)
    })
  }

  // ─── Total Value (refined by second filter) ────────────────────────────────
  const calculateTotalStocks = () => {
    return filterStocks().reduce((acc, stock) => {
      const noteMatch = stock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? stock.stockItems
        : stock.stockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return acc + itemsAfterSecond.reduce((sum, i) => sum + i.qty * i.price, 0)
    }, 0)
  }

  const calculateTotalQtys = () => {
    return filterStocks().reduce((acc, stock) => {
      const noteMatch = stock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? stock.stockItems
        : stock.stockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return acc + itemsAfterSecond.reduce((sum, i) => sum + i.qty, 0)
    }, 0)
  }

  // ─── Excel Export (refined by second filter) ───────────────────────────────
  const exportToExcel = () => {
    const rows = filterStocks().flatMap((stock) => {
      const noteMatch = stock.note
        ?.toLowerCase()
        .includes(filter.toLowerCase())

      const itemsAfterFirst = noteMatch
        ? stock.stockItems
        : stock.stockItems.filter((i) =>
            i.name.toLowerCase().includes(filter.toLowerCase())
          )

      const itemsAfterSecond = itemsAfterFirst.filter((i) =>
        i.name.toLowerCase().includes(refineFilter.toLowerCase())
      )

      return itemsAfterSecond.map((i) => ({
        Date: new Date(stock.date).toLocaleDateString(),
        Product: i.name,
        Quantity: i.qty,
        Total: (i.qty * i.price).toFixed(2),
        Note: stock.note || 'N/A'
      }))
    })

    const ws = XLSX.utils.json_to_sheet(rows)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, 'Stocks')
    XLSX.writeFile(wb, 'Stocks_Report.xlsx')
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Row className="align-items-center mb-3">
        <Col>
          <h1>Stocks</h1>
          <Button
            variant="success"
            className="my-3 mx-2"
            onClick={exportToExcel}
          >
            Export to Excel
          </Button>

          <Link to='/admin/stocksearchReplace' className='btn btn-light my-3'>
            Search
          </Link>
          <Link to="/admin/stocksearchdelete" className="btn btn-light">
          Del
          </Link>
          <Link to='/admin/stocknotesearchReplace' className='btn btn-light my-3'>
            NoteSearch
          </Link>
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Filter by name or note"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>

        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Refine by product name"
            value={refineFilter}
            onChange={(e) => setRefineFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={3}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={6} className="text-end">
          <h4>Total Qty: {calculateTotalQtys().toFixed(2)}</h4>
          <h4>Total Stocks: ${calculateTotalStocks().toFixed(2)}</h4>
        </Col>
      </Row>

      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">
          {error?.data?.message || 'Error loading stocks'}
        </Message>
      ) : (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th>DATE</th>
                <th>PRODUCT</th>
                <th>QTY</th>
                <th>TOTAL</th>
                <th>NOTE</th>
                <th>IMAGE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>

            <tbody>
              {filterStocks().map((stock) => {
                const noteMatch = stock.note
                  ?.toLowerCase()
                  .includes(filter.toLowerCase())

                const itemsAfterFirst = noteMatch
                  ? stock.stockItems
                  : stock.stockItems.filter((i) =>
                      i.name.toLowerCase().includes(filter.toLowerCase())
                    )

                const itemsAfterSecond = itemsAfterFirst.filter((i) =>
                  i.name.toLowerCase().includes(refineFilter.toLowerCase())
                )

                const sorted = [...itemsAfterSecond].sort((a, b) =>
                  a.name.localeCompare(b.name)
                )

                return sorted.map((item) => (
                  <tr key={`${stock._id}-${item.name}`}>
                    <td>{new Date(stock.date).toLocaleDateString()}</td>
                    <td>{item.name}</td>
                    <td>{item.qty}</td>
                    <td>${(item.qty * item.price).toFixed(2)}</td>
                    <td>{stock.note || 'N/A'}</td>
                    <td>
                      {item.images?.[0] && (
                        <img
                          src={item.images[0]}
                          alt="thumb"
                          className="thumbnail-img me-2"
                          style={{
                            width: '10px',
                            height: '10px',
                            objectFit: 'cover',
                            borderRadius: '5px'
                          }}
                        />
                      )}
                    </td>
                    <td>
                      <Button
                        as={Link}
                        to={`/stock/${stock._id}`}
                        variant="light"
                        className="btn-sm"
                      >
                        Details
                      </Button>
                      <Button
                        as={Link}
                        to={`/admin/stock/${stock._id}/edit`}
                        variant="light"
                        className="btn-sm mx-2"
                      >
                        Edit
                      </Button>
                      <Button
                          as={Link}
                         to={`/admincopy/stock/${stock._id}/copy`}
                        variant="light"
                        className="btn-sm mx-2"
                       >
                      Copy
                      </Button>
                      <Button
                        variant="danger"
                        className="btn-sm mx-2"
                        onClick={() => deleteHandler(stock._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              })}
            </tbody>
          </Table>

          <Pagination>
            {[...Array(data.pages).keys()].map((x) => (
              <Pagination.Item
                key={x + 1}
                active={x + 1 === pageNumber}
                onClick={() => paginationHandler(x + 1)}
              >
                {x + 1}
              </Pagination.Item>
            ))}
          </Pagination>
        </>
      )}
    </>
  )
}

export default StockListScreen
